import type { components } from '@tallerya/api-client';
import type { ProblemaApi } from '@tallerya/i18n';
import { keepPreviousData, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '../../api';
import { useTallerDeLaCache } from '../../taller/ProveedorTaller';

// Lo que las pantallas de activos (#119) le piden a la API, con la forma de
// `sucursales/datos.ts`. Los tipos, las marcas y los modelos se leen con los
// `datos.ts` de sus propias pantallas; acá van los activos y la búsqueda de
// clientes del formulario.

export type Activo = components['schemas']['AssetListItem'];
export type FichaActivo = components['schemas']['AssetDetail'];
export type ClienteEnLista = components['schemas']['CustomerListItem'];
export type Campo = components['schemas']['AssetTypeFieldItem'];
export type ActivoExistente = components['schemas']['ExistingAsset'];
/** El 409 del alta y la edición: con `asset.identificador_tomado` trae el activo que ya tiene la placa (#147). */
export type ProblemaActivo = ProblemaApi & { existingAsset?: ActivoExistente | null };
/** El 409 de la baja: con `asset.tiene_historial` trae cuántas órdenes tiene y si sigue activo (#147). */
export type ProblemaBajaActivo = ProblemaApi & { orderCount?: number | null; isActive?: boolean | null };

export interface FiltrosActivos {
  buscar: string;
  /** Un id de tipo, o `todos`. */
  tipo: string;
  estado: 'todos' | 'activos' | 'inactivos';
  cursor: string | null;
}

/// El taller de la pestaña (#205), en toda clave de caché: regla 5 de multitenancy.
function useTaller(): string {
  return useTallerDeLaCache();
}

const claves = {
  todo: (taller: string) => ['activos', taller] as const,
  lista: (taller: string, filtros: FiltrosActivos) => ['activos', taller, 'lista', filtros] as const,
  ficha: (taller: string, id: string) => ['activos', taller, 'ficha', id] as const,
  // Bajo `activos`: guardar, activar o desactivar invalidan esa rama, y el
  // historial se relee solo.
  historial: (taller: string, id: string) => ['activos', taller, 'historial', id] as const,
  codigo: (taller: string) => ['activos', taller, 'codigo-propuesto'] as const,
  clientes: (taller: string, texto: string) => ['clientes', taller, 'busqueda', texto] as const,
  cliente: (taller: string, id: string) => ['clientes', taller, 'nombre', id] as const,
};

/** La versión llega como `number | string` —un `uint` en el contrato—. Se manda como número. */
export const version = (v: number | string) => Number(v);

export function useActivos(filtros: FiltrosActivos) {
  const taller = useTaller();

  return useQuery({
    queryKey: claves.lista(taller, filtros),
    queryFn: async () => {
      const { data, error } = await api.GET('/api/assets', {
        params: {
          query: {
            ...(filtros.buscar.trim() && { search: filtros.buscar.trim() }),
            ...(filtros.tipo !== 'todos' && { assetTypeId: filtros.tipo }),
            ...(filtros.estado !== 'todos' && { isActive: filtros.estado === 'activos' }),
            ...(filtros.cursor && { cursor: filtros.cursor }),
          },
        },
      });
      if (error || !data) throw new Error('No se pudo leer la lista de activos');
      return data;
    },
    placeholderData: keepPreviousData,
  });
}

export function useFichaActivo(id: string | undefined) {
  const taller = useTaller();

  return useQuery({
    queryKey: claves.ficha(taller, id ?? ''),
    enabled: id !== undefined,
    queryFn: async () => {
      const { data, error, response } = await api.GET('/api/assets/{id}', {
        params: { path: { id: id ?? '' } },
      });
      // El 404 no es un fallo de red: el activo no existe o es de otro taller.
      if (response.status === 404) return null;
      if (error || !data) throw new Error('No se pudo leer el activo');
      return data;
    },
    // Siempre fresca: con la versión vieja en caché, guardar chocaría.
    staleTime: 0,
  });
}

/**
 * El historial del activo (#187): quién lo registró y quién le cambió qué, lo más
 * nuevo primero. Pide lo mismo que la ficha, `customer.edit`.
 *
 * Solo se pide con el panel abierto: no al abrir la ficha (#193).
 */
export function useHistorialActivo(id: string, abierto: boolean) {
  const taller = useTaller();

  return useQuery({
    queryKey: claves.historial(taller, id),
    enabled: abierto,
    queryFn: async () => {
      const { data, error } = await api.GET('/api/assets/{id}/history', { params: { path: { id } } });
      if (error || !data) throw new Error('No se pudo leer el historial del activo');
      return data;
    },
  });
}

/** El código que tendría el activo nuevo. Solo se muestra: el alta lo pide al guardar. */
export function useCodigoPropuesto(habilitado: boolean) {
  const taller = useTaller();

  return useQuery({
    queryKey: claves.codigo(taller),
    enabled: habilitado,
    staleTime: 0,
    queryFn: async () => {
      const { data, error } = await api.GET('/api/assets/next-code');
      if (error || !data) throw new Error('No se pudo leer el código propuesto');
      return data.code;
    },
  });
}

/** Los clientes que coinciden con lo escrito, para elegir el dueño del activo. Pocos: es un buscador. */
export function useBuscarClientes(texto: string) {
  const taller = useTaller();
  const limpio = texto.trim();

  return useQuery({
    queryKey: claves.clientes(taller, limpio),
    enabled: limpio.length >= 2,
    queryFn: async () => {
      const { data, error } = await api.GET('/api/customers', {
        params: { query: { search: limpio, isActive: true, limit: 8 } },
      });
      if (error || !data) throw new Error('No se pudieron buscar los clientes');
      return data.items;
    },
    placeholderData: keepPreviousData,
  });
}

/**
 * El nombre del cliente que llega en la dirección (`/activos/nuevo?cliente=…`),
 * desde la ficha del cliente (#105). Se busca su ficha solo para mostrar a quién
 * se le registra el activo.
 */
export function useNombreDeCliente(id: string | null) {
  const taller = useTaller();

  return useQuery({
    queryKey: claves.cliente(taller, id ?? ''),
    enabled: id !== null,
    queryFn: async () => {
      const { data, error, response } = await api.GET('/api/customers/{id}', {
        params: { path: { id: id ?? '' } },
      });
      if (response.status === 404) return null;
      if (error || !data) throw new Error('No se pudo leer el cliente');
      return data.name;
    },
  });
}

/** Lo que manda el formulario, ya convertido: el detalle con números y sí/no de verdad. */
export interface EnvioActivo {
  assetTypeId: string;
  code: string;
  identifier: string;
  customerId: string;
  brandId: string;
  modelId: string;
  year: string;
  notes: string;
  details: Record<string, string | number | boolean>;
}

/** Lo vacío se omite: un id opcional no admite `null` en el contrato (ver `TypedIdSchemaTransformer`). */
const siHay = <K extends string>(clave: K, valor: string) =>
  valor.trim() ? ({ [clave]: valor.trim() } as Record<K, string>) : {};

/**
 * Las escrituras. Cada una devuelve el `ProblemDetails` o nada, la forma que
 * espera `useFormulario`, y **siempre** invalida lo de activos: con o sin error,
 * la versión que hay que mandar la próxima vez puede haber cambiado.
 */
export function useCambiosActivos() {
  const taller = useTaller();
  const cache = useQueryClient();

  async function tras<T extends { error?: unknown }>(peticion: Promise<T>): Promise<ProblemaApi | undefined> {
    const { error } = await peticion;
    await cache.invalidateQueries({ queryKey: claves.todo(taller) });
    return error ? (error as ProblemaApi) : undefined;
  }

  return {
    /**
     * `code` vacío —la persona no tocó el propuesto— deja que lo ponga el
     * sistema. `alCrear` recibe el id del activo nuevo.
     */
    crear: async (v: EnvioActivo, alCrear?: (id: string) => void) => {
      const { data, error } = await api.POST('/api/assets', {
        body: {
          assetTypeId: v.assetTypeId,
          ...siHay('code', v.code),
          ...siHay('identifier', v.identifier),
          ...siHay('customerId', v.customerId),
          ...siHay('brandId', v.brandId),
          ...siHay('modelId', v.modelId),
          ...siHay('notes', v.notes),
          ...(v.year.trim() && { year: Number(v.year) }),
          details: v.details,
        },
      });
      if (data) alCrear?.(String(data.id));
      await cache.invalidateQueries({ queryKey: claves.todo(taller) });
      return error ? (error as ProblemaApi) : undefined;
    },

    editar: (id: string, versionActual: number, v: EnvioActivo) =>
      tras(
        api.PUT('/api/assets/{id}', {
          params: { path: { id } },
          body: {
            version: versionActual,
            code: v.code.trim(),
            ...siHay('identifier', v.identifier),
            ...siHay('customerId', v.customerId),
            ...siHay('brandId', v.brandId),
            ...siHay('modelId', v.modelId),
            ...siHay('notes', v.notes),
            ...(v.year.trim() && { year: Number(v.year) }),
            details: v.details,
          },
        }),
      ),

    cambiarEstado: (id: string, activo: boolean) =>
      tras(api.PUT('/api/assets/{id}/status', { params: { path: { id } }, body: { isActive: activo } })),

    eliminar: async (id: string) => {
      const { error } = await api.DELETE('/api/assets/{id}', { params: { path: { id } } });
      // Todo lo de activos menos la ficha que se acaba de eliminar y su historial:
      // pedirlos de nuevo daría un 404, y la ficha abierta diría «no existe»
      // justo mientras se sale. Si no se eliminó, se relee todo, como siempre.
      const eliminadas = error ? [] : [claves.ficha(taller, id), claves.historial(taller, id)];
      await cache.invalidateQueries({
        queryKey: claves.todo(taller),
        predicate: (consulta) =>
          !eliminadas.some((clave) => clave.every((parte, i) => consulta.queryKey[i] === parte)),
      });
      return error ? (error as ProblemaApi) : undefined;
    },
  };
}
